import { useState } from 'react';
import { Box, TextField, Typography, Collapse, Tooltip, IconButton } from '@mui/material';
import { StatusFilter } from './StatusFilter/StatusFilter';
import { SearchBar } from './SearchBar/SearchBar';
import StatusCircleFilter from './StatusCircleFilter/StatusCircleFilter';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useFiltersStore } from '../../store/filtersStore';
import { useEstadosStore } from '../../store/estadosStore';
import { capitalizeFirstLetter } from '../../utils/stringUtils';
import RefreshButton from '../RefreshButton/RefreshButton';
import { useAuthReady } from '../../hooks/useAuthReady';


interface FiltersProps {
  onSearch?: (value: string) => void;
  onRefresh: () => void;
  refreshTooltip?: string;
  entityType?: 'licitaciones' | 'compras-agiles';
}

export const Filters: React.FC<FiltersProps> = ({
  onSearch,
  onRefresh,
  refreshTooltip = "Actualizar datos",
  entityType = 'licitaciones'
}) => {
  const {
    activeStatusFilters,
    clearAllFilters,
    setSearchTerm,
    searchTerm,
    postuladoFilter,
    adjudicadoFilter,
    noLeidasFilter,
    fechaCierreDesde,
    fechaCierreHasta,
    fechaPublicacionDesde,
    fechaPublicacionHasta,
    setFechaCierreDesde,
    setFechaCierreHasta,
    setFechaPublicacionDesde,
    setFechaPublicacionHasta,
  } = useFiltersStore();
  const { estadosMPInverso } = useEstadosStore();
  const isAuthReady = useAuthReady();
  const [showDateFilters, setShowDateFilters] = useState(false);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleClearFilters = () => {
    clearAllFilters();
  };

  const handleRefresh = () => {
    if (isAuthReady) {
      onRefresh();
    }
  };

  const hasActiveDateFilters =
    fechaCierreDesde !== null ||
    fechaCierreHasta !== null ||
    fechaPublicacionDesde !== null ||
    fechaPublicacionHasta !== null;

  const hasAnyActiveFilter =
    activeStatusFilters.length > 0 ||
    searchTerm.trim() !== '' ||
    postuladoFilter === true ||
    adjudicadoFilter === true ||
    noLeidasFilter === true ||
    hasActiveDateFilters;

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <SearchBar onSearch={handleSearch} value={searchTerm} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <StatusFilter entityType={entityType} />
          <StatusCircleFilter
            status={3}
            color="#4caf50"
            tooltip="Filtrar por seguidas"
            label="Seguidas"
          />
          <StatusCircleFilter
            status={2}
            color="#ff9800"
            tooltip="Filtrar por pendientes"
            label="Pendientes"
          />
          <StatusCircleFilter
            status={4}
            color="#f44336"
            tooltip="Filtrar por descartadas"
            label="Descartadas"
          />
          <StatusCircleFilter
            status="others"
            color="#9e9e9e"
            tooltip="Filtrar por analizando"
            label="Analizando"
          />
          <Box
            sx={{
              height: '32px',
              borderLeft: '2px solid #E6E6E6',
              mx: 1,
              display: 'none',
              '@media (min-width: 1024px)': {
                display: 'block'
              }
            }}
          />
          <StatusCircleFilter
            status="postulado"
            color="#2196f3"
            tooltip="Filtrar por postuladas"
            label="Postulado"
            filterType="postulado"
          />
          <StatusCircleFilter
            status="adjudicado"
            color="#4caf50"
            tooltip="Filtrar por adjudicadas"
            label="Adjudicado"
            filterType="adjudicado"
          />
          <StatusCircleFilter
            status="no-leidas"
            color="#000000"
            tooltip="Filtrar por no leídas"
            label="No Leídas"
            filterType="no-leidas"
          />
        </Box>

        <Box
          sx={{
            height: '32px',
            borderLeft: '2px solid #E6E6E6',
            mx: 1,
            display: 'none',
            '@media (min-width: 1024px)': {
              display: 'block'
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(estadosMPInverso).map(([codigo, nombre]) => (
            <StatusCircleFilter
              key={codigo}
              status={`codigo_estado_${codigo}`}
              color="transparent"
              tooltip={`Filtrar por ${nombre.toLowerCase()}`}
              label={capitalizeFirstLetter(nombre)}
            />
          ))}
          <Tooltip title="Filtros avanzados de fecha" arrow>
            <IconButton
              size="small"
              onClick={() => setShowDateFilters((prev) => !prev)}
              sx={{
                border: `1px solid ${hasActiveDateFilters ? '#1976d2' : '#E6E6E6'}`,
                borderRadius: 1,
                padding: '4px 8px',
                backgroundColor: hasActiveDateFilters ? '#e3f2fd' : 'white',
                color: hasActiveDateFilters ? '#1976d2' : '#666',
                '&:hover': { backgroundColor: '#f0f7ff' },
              }}
            >
              <FilterListIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {hasAnyActiveFilter && (
            <Tooltip title="Limpiar búsqueda y filtros" arrow>
              <Box
                onClick={handleClearFilters}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  backgroundColor: 'white',
                  border: '1px solid #E6E6E6',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <ClearIcon sx={{ fontSize: 16, color: '#666' }} />
              </Box>
            </Tooltip>
          )}
          <RefreshButton
            onRefresh={handleRefresh}
            tooltip={refreshTooltip}
          />
        </Box>
      </Box>

      <Collapse in={showDateFilters}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            p: 2,
            backgroundColor: '#f9f9f9',
            border: '1px solid #E6E6E6',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
              Fecha de cierre
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Desde"
                type="date"
                size="small"
                value={fechaCierreDesde ?? ''}
                onChange={(e) => setFechaCierreDesde(e.target.value || null)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                value={fechaCierreHasta ?? ''}
                onChange={(e) => setFechaCierreHasta(e.target.value || null)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 160 }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              height: 'auto',
              borderLeft: '2px solid #E6E6E6',
              mx: 1,
              display: 'none',
              '@media (min-width: 768px)': { display: 'block' },
            }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
              Fecha de publicación
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Desde"
                type="date"
                size="small"
                value={fechaPublicacionDesde ?? ''}
                onChange={(e) => setFechaPublicacionDesde(e.target.value || null)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                value={fechaPublicacionHasta ?? ''}
                onChange={(e) => setFechaPublicacionHasta(e.target.value || null)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 160 }}
              />
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
